/**
 * @fileoverview One plant, at a glance: a picture, a name, where it is and how far along.
 *
 * The card is the right way to read one plant and the wrong way to read
 * twenty — it is four hundred pixels tall, so a garden of twenty is eight
 * screens of scrolling. This is the same plant in a sixth of the space, and it
 * is still a link to everything the card offers.
 *
 * The name is the backend's one name (lib/plantName, services/grow/
 * plantNaming), with the day it was added in bold when another plant of the
 * same name is in the same bed (NameParts); the bed itself is at the foot,
 * not repeated in the name. The week's state and its icons sit
 * in one badge on the picture (see PlantMarks). Every tile in a row is the
 * row's height, with the bed at its foot, so the beds line up across a row
 * whether a name takes one line or two.
 *
 * Where: PlantGrid's grid view.
 * API: none of its own; the week's badge reads the plants brief (usePlantsBrief).
 * Settings: none.
 * Words (en/es): plants.tile.open, plants.tile.restore, plants.archive.delete.open,
 * plants.form.deleteForeverEllipsis, plants.card.state.*.
 *
 * @module PlantTile
 */

import React from 'react';
import { MapPin, Trash2, Undo2 } from 'lucide-react';
import { Plant } from '@/shared/types/plant';
import { StageId } from '@/shared/types/stage';
import { getStageTheme } from '@/shared/theme/stageThemes';
import { plantCoverUrl } from '../lib/plantPhotoUrl';
import { brandConfig } from '@/shared/styles/base';
import { cn } from '@/lib/utils';
import { FramedPhoto } from '@/shared/components/ui/FramedPhoto';
import { Pill } from '@/shared/components/ui/Pill';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { usePlantsBrief } from '@/shared/hooks/usePlantsBrief';
import { PlantMarks } from './card/PlantMarks';
import { NameParts } from '@/shared/components/NameParts';
import { plantShown, plantTitle } from '@/shared/lib/plantName';

/** The plant a tile shows, and what its buttons do. */
interface PlantTileProps {
  /** The plant. */
  plant: Plant;
  /** Opens the plant full size. The tile's whole picture and name do this. */
  onOpen: (plant: Plant) => void;
  /**
   * The date the current ordering is based on, ready to read.
   *
   * Shown only when the garden is sorted by a date: an order you cannot see
   * the basis of looks like no order at all.
   */
  sortedOn?: string | null;
  /** True when this plant has been taken out of the garden. */
  archived?: boolean;
  /** Puts it back. Offered on an archived plant, which a card cannot restore. */
  onRestore?: (plant: Plant) => void;
  /** True while a restore is on its way, so it cannot be sent twice. */
  isRestoring?: boolean;
  /** Asks to delete it for good (the caller opens the type-DELETE confirm). Offered on an archived plant. */
  onDelete?: (plant: Plant) => void;
}

/**
 * One plant as a tile. The picture and the name open it full size; an
 * archived tile also offers Restore. A tile is a card in less space, not a
 * different kind of thing.
 *
 * @param props - PlantTileProps.
 */
export const PlantTile: React.FC<PlantTileProps> = ({
  plant, onOpen, sortedOn, archived, onRestore, isRestoring, onDelete,
}) => {
  const { reactTranslate: t } = useTranslation();
  /** The picture: the plant's cover, at card size. */
  const cover = plantCoverUrl(plant, 'card');
  /** Its stage now, for the badge on the picture: the server's (null: none recorded). */
  const stage = plant.stage;
  /** The stage's colours. */
  const theme = getStageTheme(stage?.id as StageId);
  const { forPlant } = usePlantsBrief();
  // What the week holds, as a badge on the picture. None for a sheltered bed,
  // which the weather does not reach, or for a plant out of the garden.
  const week = !plant.location.sheltered && !archived ? forPlant(plant.plant_obj_id)?.week ?? null : null;
  /** The name as the backend says it (lib/plantName), with the day added in bold when a twin shares its bed. */
  // The bed is at the tile's foot: not said again in the name.
  const title = plantTitle(plant, { bedShown: true });
  /** The name as text, for the tile's labels and the picture's alt. */
  const shown = plantShown(plant);
  // The badge is hidden from screen readers, so its state is said here.
  const openLabel = t('plants.tile.open', { name: shown })
    + (week ? `, ${t(`plants.card.state.${week.state}`)}` : '');

  return (
    <div className={cn(
      'group relative flex h-full flex-col overflow-hidden rounded-sm border bg-card shadow-sm transition-shadow hover:shadow-md',
      // Dimmed, because it is not in the garden — still findable, plainly past.
      archived && 'opacity-70'
    )}>
      {/*
        * The picture and the name open the plant; Restore below, on an
        * archived tile, does its own thing. A button rather than a link,
        * because this goes nowhere — it opens what you are already looking
        * at, larger.
        */}
      <button
        type="button"
        onClick={() => onOpen(plant)}
        aria-label={openLabel}
        className="block w-full text-left"
      >
        <div className="relative w-full pb-[100%]">
          {cover ? (
            <FramedPhoto src={cover} alt={shown} loading="lazy" />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center bg-muted">
              <brandConfig.emptyStateIcon className="h-8 w-8 text-muted-foreground" />
            </div>
          )}
          <PlantMarks week={week} onPicture className="absolute right-1.5 top-1.5 max-w-[calc(100%-0.75rem)]" />
          {stage && <Pill size="sm" theme={theme} className="absolute bottom-1.5 left-1.5 max-w-[calc(100%-0.75rem)]">{stage.name}</Pill>}
        </div>

        {/* The name gets the whole width. Sharing a line with two icons left
            it about a hundred pixels on a phone, which turned "Cherry Sweet
            100 Tomato" into "Cherry Sw..." — a tile that identifies nothing.
            The icons sit with the bed instead, which is short. */}
        <div className="px-2 pt-2">
          <p className="line-clamp-2 text-sm font-medium leading-snug">
            <NameParts parts={title} />
          </p>
          {sortedOn && (
            <p className="truncate text-[11px] text-muted-foreground">{sortedOn}</p>
          )}
        </div>
      </button>

      {/* At the foot of the tile, so the beds line up across a row. */}
      <div className="mt-auto px-2 pb-2">
        <div className="flex items-center justify-between gap-1">
            <p className="flex min-w-0 items-center gap-1 text-xs text-muted-foreground">
              <MapPin className="h-3 w-3 shrink-0" aria-hidden="true" />
              <span className="truncate">{plant.location.name}</span>
            </p>
            {/*
              * Restore, and nothing else.
              *
              * There used to be a pencil here too, twenty pixels square,
              * sitting twenty-nine pixels from a tile of a hundred and
              * eighty-eight by two hundred and sixty-seven — a thumb-sized
              * miss away from a target with a different outcome, and well
              * under the size a thumb can reliably hit in the first place.
              * Opening the tile gives the card, and the card carries the
              * pencil; the shortcut only skipped a screen you were about to
              * see. Restore stays because the card cannot do it.
              */}
            {archived && onRestore && (
              <div className="flex shrink-0 items-center gap-0.5">
                <button
                  type="button"
                  onClick={() => onRestore(plant)}
                  disabled={isRestoring}
                  aria-label={t('plants.tile.restore', { name: shown })}
                  className="rounded-sm p-0.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground disabled:opacity-50"
                >
                  <Undo2 className="h-3.5 w-3.5" aria-hidden="true" />
                </button>
                {onDelete && (
                  <button
                    type="button"
                    onClick={() => onDelete(plant)}
                    disabled={isRestoring}
                    aria-label={t('plants.archive.delete.open', { name: shown })}
                    title={t('plants.form.deleteForeverEllipsis')}
                    className="rounded-sm p-0.5 text-destructive transition-colors hover:bg-destructive/10 disabled:opacity-50"
                  >
                    <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                  </button>
                )}
              </div>
            )}
        </div>
      </div>
    </div>
  );
};
